import { useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { cardTitleTextClass, ghostBtnClass, inputClass } from "@/components/common/surfaceClasses";
import {
  filterAudienceFacts,
  formatAmount,
  matchablePct,
  paginateRows,
  uniqueSorted,
  type QualityPageSize,
} from "@/components/audience-data/audienceDataFormat";
import { AudienceDataModeSlider } from "@/components/audience-data/AudienceDataModeSlider";
import { AudienceDataPager } from "@/components/audience-data/AudienceDataPager";
import { AudienceDataQualityMenu } from "@/components/audience-data/AudienceDataQualityMenu";
import { AudienceDataUnmatchablePanel } from "@/components/audience-data/AudienceDataUnmatchablePanel";
import type { SsdQualityIssue, SsdQualitySummary, SsdSaleFact } from "@/lib/ssdSalesClient";

type ListMode = "sales" | "unmatchable";

function SalesTable({ facts }: { facts: SsdSaleFact[] }) {
  if (facts.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">No sales match these filters.</p>;
  }
  return (
    <div className="min-w-0 overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
            <th className="py-2 pr-3 font-medium">Date</th>
            <th className="py-2 pr-3 font-medium">Name</th>
            <th className="py-2 pr-3 font-medium">Order</th>
            <th className="py-2 pr-3 font-medium">Email</th>
            <th className="py-2 pr-3 font-medium">Phone</th>
            <th className="py-2 pr-3 font-medium">Amount</th>
            <th className="py-2 pr-3 font-medium">Dealer</th>
            <th className="py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {facts.map((row) => (
            <tr key={row.id} className="border-b border-gray-100 dark:border-zinc-800">
              <td className="whitespace-nowrap py-1.5 pr-3">{row.sale_date ?? "—"}</td>
              <td className="py-1.5 pr-3">{row.customer_full_name || "—"}</td>
              <td className="whitespace-nowrap py-1.5 pr-3">{row.order_number || "—"}</td>
              <td className="py-1.5 pr-3">{row.customer_email || "—"}</td>
              <td className="py-1.5 pr-3">{row.customer_phone || "—"}</td>
              <td className="whitespace-nowrap py-1.5 pr-3">{formatAmount(row.gross_sale_amount)}</td>
              <td className="py-1.5 pr-3">{row.source_dealer_name || "—"}</td>
              <td className="py-1.5">
                {row.status || "—"}
                {row.is_new ? " · New" : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AudienceDataSalesCard(props: {
  facts: SsdSaleFact[];
  issues: SsdQualityIssue[];
  quality: SsdQualitySummary | null;
  skippedLastJob: number;
  newCount: number;
  dateFrom: string;
  dateTo: string;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  acknowledging: boolean;
  onAcknowledgeNew: () => Promise<void>;
}) {
  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState<ListMode>("sales");
  const [dealer, setDealer] = useState("");
  const [status, setStatus] = useState("");
  const [matchableOnly, setMatchableOnly] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<QualityPageSize>(10);

  const dealers = useMemo(() => uniqueSorted(props.facts.map((f) => f.source_dealer_name)), [props.facts]);
  const statuses = useMemo(() => uniqueSorted(props.facts.map((f) => f.status)), [props.facts]);
  const filtered = useMemo(
    () => filterAudienceFacts(props.facts, { dealer, status, matchableOnly, newOnly, q }),
    [props.facts, dealer, status, matchableOnly, newOnly, q]
  );
  const paged = paginateRows(filtered, page, pageSize);
  const listMatchable = filtered.filter((f) => f.email_valid || f.phone_valid).length;
  const newCount = props.newCount;

  return (
    <SectionCard
      className="min-w-0 overflow-hidden"
      title={
        <div className="min-w-0 space-y-2">
          <h3 className={`text-sm font-semibold ${cardTitleTextClass}`}>Sales data</h3>
          <AudienceDataModeSlider
            value={mode}
            onChange={(m) => {
              setMode(m);
              setPage(1);
            }}
            ariaLabel="Sales or unmatchable"
            tabs={[
              { id: "sales", label: "Sales" },
              { id: "unmatchable", label: `Unmatchable (${props.issues.length})`, alert: props.issues.length > 0 },
            ]}
          />
        </div>
      }
      description={
        mode === "sales"
          ? "Same date range as the quality alert. New is live Sales Feed inbound since last acknowledge — CSV backfill is never New."
          : "Rows that drop out of matchable audiences. Sales and unmatchable are separate views."
      }
      rightSlot={
        <div className="flex shrink-0 items-center gap-2">
          <AudienceDataQualityMenu
            quality={props.quality}
            issueCount={props.issues.length}
            skippedLastJob={props.skippedLastJob}
          />
          <button type="button" className={ghostBtnClass} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? "Collapse" : "Expand"}
          </button>
        </div>
      }
    >
      <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="min-w-0 text-sm text-gray-700 dark:text-zinc-300">
          From
          <input
            type="date"
            className={`mt-1 ${inputClass}`}
            value={props.dateFrom}
            onChange={(e) => props.setDateFrom(e.target.value)}
          />
        </label>
        <label className="min-w-0 text-sm text-gray-700 dark:text-zinc-300">
          To
          <input
            type="date"
            className={`mt-1 ${inputClass}`}
            value={props.dateTo}
            onChange={(e) => props.setDateTo(e.target.value)}
          />
        </label>
      </div>
      {open && mode === "sales" ? (
        <>
          <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="min-w-0 text-sm text-gray-700 dark:text-zinc-300">
              Dealer
              <select
                className={`mt-1 ${inputClass}`}
                value={dealer}
                onChange={(e) => {
                  setDealer(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All dealers</option>
                {dealers.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-0 text-sm text-gray-700 dark:text-zinc-300">
              Status
              <select
                className={`mt-1 ${inputClass}`}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All statuses</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block min-w-0 text-sm text-gray-700 dark:text-zinc-300">
            Search name, email, phone, or order
            <input
              type="search"
              className={`mt-1 ${inputClass}`}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Filter rows"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={matchableOnly}
              onChange={(e) => {
                setMatchableOnly(e.target.checked);
                setPage(1);
              }}
            />
            Matchable only (valid email or phone)
          </label>
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
              <input
                type="checkbox"
                checked={newOnly}
                onChange={(e) => {
                  setNewOnly(e.target.checked);
                  setPage(1);
                }}
              />
              New only ({newCount})
            </label>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={props.acknowledging || newCount === 0}
              onClick={() => {
                if (
                  window.confirm(
                    `Mark ${newCount} new sale${newCount === 1 ? "" : "s"} as reviewed? Later live arrivals will show as New again.`
                  )
                ) {
                  void props.onAcknowledgeNew();
                }
              }}
            >
              {props.acknowledging ? "Acknowledging…" : "Acknowledge new"}
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-300">
            Showing {filtered.length} of {props.facts.length} row{props.facts.length === 1 ? "" : "s"};{" "}
            {listMatchable} matchable ({matchablePct(listMatchable, filtered.length)}).
          </p>
          <AudienceDataPager
            page={paged.page}
            pages={paged.pages}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
          <SalesTable facts={paged.items} />
        </>
      ) : null}
      {open && mode === "unmatchable" ? <AudienceDataUnmatchablePanel issues={props.issues} /> : null}
    </SectionCard>
  );
}
